import { Typography } from "@/components";
import {
	BackAppBar,
	MessageInput,
	MessageItem,
	MessageViewOnlyRemark,
} from "@/components/ui";
import useMessageMutation from "@/hooks/useMessageMutation";
import useProfile from "@/hooks/useProfile";
import { cn } from "@/libraries/cn";
import { CASE_MEMBER_TYPE, QUERY_KEYS } from "@/libraries/constant";
import { fromUnixToDateString } from "@/libraries/date";
import { capitalized, getMessageTagLabel, randUuid } from "@/libraries/helper";
import {
	getCaseQueryOptions,
	getMessageQueryOptions,
	markReadAllMessageQueryOptions,
} from "@/queries";
import {
	DataIdSchema,
	MessageDetailSearchParams,
	PostCreateMessageBodySchema,
} from "@/schemas";
import type { MessageDiscussion } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	type QueryClient,
	onlineManager,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useRouterState } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import cloneDeep from "lodash/cloneDeep";
import flatMap from "lodash/flatMap";
import groupBy from "lodash/groupBy";
import isString from "lodash/isString";
import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import type { PhotoProps } from "react-photo-gallery";
import { useLongPress } from "use-long-press";
import Lightbox from "yet-another-react-lightbox";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Download from "yet-another-react-lightbox/plugins/download";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Video from "yet-another-react-lightbox/plugins/video";
import ContextMenuMessage from "./-components/ContextMenuMessage";

export const Route = createFileRoute("/dashboard/case/message/$id")({
	params: {
		parse: (params) => ({
			id: DataIdSchema.parse(Number(params.id)),
		}),
		stringify: ({ id }) => ({ id: id.toString() }),
	},
	validateSearch: zodSearchValidator(MessageDetailSearchParams),
	loaderDeps: ({ search }) => ({
		caseId: search.cid,
	}),
	beforeLoad: async ({ context, params, search, cause }) => {
		const queryClient: QueryClient = context.queryClient;
		if (cause === "enter" && params.id && search?.cid) {
			const caseItem = await queryClient.fetchQuery(
				getCaseQueryOptions(search.cid),
			);
			if (caseItem.unread_thread_messages && caseItem.channel_name && params.id)
				await queryClient.prefetchQuery(
					markReadAllMessageQueryOptions({
						channel_name: caseItem.channel_name,
						message_id: params.id,
					}),
				);
		}
	},
	loader: ({ context, params, deps }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return [
				context.queryClient.ensureQueryData(getMessageQueryOptions(params.id)),
				context.queryClient.ensureQueryData(getCaseQueryOptions(deps.caseId)),
			];
		}
		return [
			context.queryClient.getQueryData([QUERY_KEYS.GET_MESSAGE, params.id]),
			context.queryClient.getQueryData([QUERY_KEYS.GET_CASE, deps.caseId]),
		];
	},
	component: RouteComponent,
});

function RouteComponent() {
	const navigation = Route.useNavigate();
	const qSenderName = Route.useSearch({
		select: (s) => s.sender,
	});
	const caseId = Route.useSearch({
		select: (s) => s.cid as number,
	});
	const messageId = Route.useParams({
		select: (s) => s.id,
	});
	const fileIdx = Route.useSearch({
		select: (s) => s["file-idx"],
	});
	const lightBoxes = useRouterState({
		// @ts-expect-error
		select: (s) => (s.location.state.lightBoxes || []) as Array<PhotoProps>,
	});

	const [cursorPosition, setCursorPosition] = useState<Record<
		"x" | "y",
		number
	> | null>(null);
	const [contextMessage, setContextMessage] =
		useState<MessageDiscussion | null>(null);

	const { profile } = useProfile();

	const scrollRef = useRef<HTMLDivElement>(null);

	const form = useForm<PostCreateMessageBodySchema>({
		mode: "all",
		resolver: zodResolver(PostCreateMessageBodySchema),
		defaultValues: async () => ({
			channel_name: messageQuery.data.message.channel_name,
			messageable_id: messageQuery.data.message.id_db.toString(),
			messageable_type: "Message",
		}),
	});

	const { readMessage } = useMessageMutation();
	const caseQuery = useSuspenseQuery(getCaseQueryOptions(caseId));
	const messageQuery = useSuspenseQuery(getMessageQueryOptions(messageId));

	const senderName = useMemo(
		() => qSenderName ?? messageQuery.data.message.user?.fullname,
		[qSenderName, messageQuery.data],
	);

	const isViewOnly = useMemo(
		() =>
			caseQuery.data.member_type === CASE_MEMBER_TYPE.EXTERNAL_REQUEST_MEMBER,
		[caseQuery.data],
	);

	const isAccepted = useMemo(
		() => messageQuery?.data?.message?.invitation_status === "accept",
		[messageQuery?.data?.message?.invitation_status],
	);

	const isReadOnly = useMemo(
		() => caseQuery.data.status === "closed",
		[caseQuery.data],
	);

	const message = useMemo(() => {
		if (!messageQuery.data.message.document_count)
			return messageQuery.data.message;
		const lastMessage = messageQuery.data.message.messages.find(
			(m) => m.id === messageQuery.data.message.id,
		);
		if (lastMessage) return messageQuery.data.message;
		return {
			...messageQuery.data.message,
			...(lastMessage ?? {}),
		};
	}, [messageQuery]);

	const memberIds = useMemo(() => {
		if (!caseQuery.data?.members?.length) return [];
		return caseQuery.data.members.map((m) => m.id);
	}, [caseQuery.data]);

	const messages = useMemo(() => {
		let messageItems = message.messages?.length
			? cloneDeep(message.messages)
			: [];
		if (!messageItems?.length) return messageItems;
		messageItems = messageItems
			.filter((m) => m.id !== message.id)
			.map((messageItem) => ({
				...messageItem,
				_isMe: messageItem.user?.id === profile?.id,
				_flag:
					messageItem.id_db === -1
						? ("pending" as const)
						: messageItem.id_db > 0 &&
								(!messageItem.reads?.length || // When no one read the message or
									(messageItem.reads?.length === 1 && // only the sender read the message
										messageItem.reads[0] === profile?.id)) // and the sender is the current user
							? ("sent" as const)
							: memberIds.some((id) => !messageItem.reads.includes(id)) // When not all members read the message
								? ("delivered" as const)
								: memberIds.every((id) => messageItem.reads.includes(id)) // When all members read the message
									? ("read" as const)
									: ("pending" as const),
			}));
		const orderedMessages = orderBy(messageItems, "updated_at", "asc");
		const groupedMessages = groupBy(orderedMessages, (m) =>
			fromUnixToDateString(m.updated_at),
		);
		const flatMessages = flatMap(
			groupedMessages,
			(messages, date) =>
				[
					{
						id: randUuid(10),
						text: date,
						system_message: true,
						tags: "date",
						channel_name: message.channel_name ?? "",
						id_db: 0,
						created_at: messages[0].created_at,
						updated_at: messages[0].updated_at,
						document_count: 0,
						document_size: "0.0 Kb",
						sent: false,
						delivered: false,
						strike: false,
						is_deleted: false,
						reply_to: null,
						invitation_status: null,
						latest_reply_date: null,
						reply_count: 0,
						reads: [],
						mention_name: [],
						user: null,
						parent: null,
						replies: [],
						documents: [],
						mentions: [],
						preview: null,
					},
					...messages,
				] as MessageDiscussion[],
		);
		return uniqBy(flatMessages, "id");
	}, [message, memberIds, profile?.id]);

	const messageTag = useMemo(() => {
		const tag = getMessageTagLabel(message, "cases");
		if (!tag?.length) return "";
		if (isString(tag)) {
			if (tag.toLowerCase() === "media") return "";
			return capitalized(tag);
		}
		if (Array.isArray(tag) && tag.length) return capitalized(tag[0]);
	}, [message]);

	const prevUnreadIdsRef = useRef<string[]>([]);
	const unreadMessageIds = useMemo(
		() => [
			...new Set(
				message.messages
					.filter(
						(m) =>
							!m.system_message &&
							m.user?.id !== profile?.id &&
							// @ts-expect-error
							!m.reads.includes(profile?.id),
					)
					.map((m) => m.id),
			),
		],
		[message.messages, profile],
	);

	const invitationStatus = useMemo(() => {
		if (!messageQuery?.data?.message?.invitation_status) return null;
		switch (messageQuery.data.message.invitation_status.toLowerCase()) {
			case "accept":
				return "Accepted";
			default:
				return capitalized(messageQuery.data.message.invitation_status);
		}
	}, [messageQuery.data.message.invitation_status]);

	const onOpenContextMenu = useCallback(
		(e: React.MouseEvent<HTMLDivElement>, message: MessageDiscussion) => {
			if (message.is_deleted) return;
			e.preventDefault();
			e.stopPropagation();
			setContextMessage(message);
			return setCursorPosition({ x: e.clientX, y: e.clientY });
		},
		[],
	);

	const onCloseContextMenu = useCallback(() => {
		setCursorPosition(null);
		return setContextMessage(null);
	}, []);

	const onLongPressMessage = useLongPress<HTMLDivElement, MessageDiscussion>(
		(e, meta) =>
			meta.context &&
			onOpenContextMenu(e as React.MouseEvent<HTMLDivElement>, meta.context),
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const isSameAsPrev =
			prevUnreadIdsRef.current.length === unreadMessageIds.length &&
			prevUnreadIdsRef.current.every((id, idx) => id === unreadMessageIds[idx]);

		if (!isSameAsPrev && unreadMessageIds.length > 0 && message.channel_name) {
			readMessage.mutate({
				channel_name: message.channel_name,
				message_ids: unreadMessageIds,
				message_id: message.id_db,
			});

			prevUnreadIdsRef.current = unreadMessageIds;
		}
	}, [unreadMessageIds, message.channel_name]);

	if (!profile) return;

	return (
		<>
			<BackAppBar
				className="!bg-gradient-account [&_*_]:text-white"
				title={`${senderName}: ${messageTag}`}
				titleClassName="flex-1"
				toolbarClassName="justify-between"
				rightContent={
					messageQuery.data.message.invitation_status ? (
						<div
							className={cn("py-1 px-2 rounded-md", {
								"bg-primary-light": !["rejected", "left"].includes(
									messageQuery.data.message.invitation_status.toLowerCase(),
								),
								"bg-danger-100": ["rejected", "left"].includes(
									messageQuery.data.message.invitation_status.toLowerCase(),
								),
							})}
						>
							<Typography
								fontWeight={400}
								fontSize={14}
								className={cn({
									"!text-primary": !["rejected", "left"].includes(
										messageQuery.data.message.invitation_status.toLowerCase(),
									),
									"!text-danger-300": ["rejected", "left"].includes(
										messageQuery.data.message.invitation_status.toLowerCase(),
									),
								})}
							>
								{invitationStatus}
							</Typography>
						</div>
					) : null
				}
			/>
			<div ref={scrollRef} className="bg-gray-light flex-1 overflow-auto">
				<div className="py-3 bg-zinc-200">
					<MessageItem
						profileId={profile?.id}
						message={message}
						senderName={senderName}
						className="container"
						chatModule="cases"
						enableFetchMessage
						onContextMenu={(e) => onOpenContextMenu(e, message)}
						{...onLongPressMessage(message)}
					/>
				</div>
				{messages.map((message) => (
					<MessageItem
						key={message.id}
						profileId={profile?.id}
						message={message}
						className="container !py-2"
						chatModule="cases"
						onContextMenu={(e) => onOpenContextMenu(e, message)}
						{...onLongPressMessage(message)}
					/>
				))}
			</div>
			{isViewOnly ? <MessageViewOnlyRemark /> : null}
			<FormProvider {...form}>
				{!isReadOnly && !isViewOnly && isAccepted ? (
					<div className="bg-white container py-2">
						<MessageInput
							channelType={null}
							chatModule="cases"
							inputMediaOnly={!message.document_count}
							hideInputTag={!!message.document_count}
							onSuccessSubmit={() =>
								scrollRef.current?.lastElementChild?.scrollIntoView({
									behavior: "smooth",
								})
							}
						/>
					</div>
				) : null}
				{contextMessage ? (
					<ContextMenuMessage
						isReadonly={caseQuery.data.status === "closed"}
						channelName={messageQuery.data.message.channel_name}
						moduleType="cases"
						message={contextMessage}
						profileId={profile.id}
						position={cursorPosition}
						onClose={onCloseContextMenu}
					/>
				) : null}
			</FormProvider>
			{lightBoxes.length ? (
				<Lightbox
					open={true}
					index={fileIdx}
					plugins={[Counter, Download, Thumbnails, Video]}
					slides={lightBoxes?.map((lightBox) => ({
						src: lightBox.src,
						title: lightBox.alt,
					}))}
					close={() => {
						return navigation({
							search: {
								cid: caseId,
								sender: senderName,
							},
							state: {
								lightBoxes: undefined,
							},
							replace: true,
						});
					}}
				/>
			) : null}
		</>
	);
}
